import type { MatrixCellStatus } from '../../types/attendanceGrid.types';
import { STATUS_CONFIG } from './attendanceStatusConfig';

/**
 * Attendance status chip. Colours and icons come from ./attendanceStatusConfig.
 *
 * Inline styles rather than Tailwind: both consumers are 100%-inline-style surfaces.
 * Not built on Badge, whose six tones collapse WFH and Travel into one and which has
 * no icon-only mode.
 */

const SR_ONLY: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export interface StatusChipProps {
  status: MatrixCellStatus;
  /** `chip` = icon + label pill. `token` = compact, for a matrix cell. */
  variant?: 'chip' | 'token';
  /** Leave type colour, drawn as a bottom rule so the type stays identifiable at token size. */
  accentColour?: string | null;
  /** Folded into the accessible name, e.g. the leave type. */
  srSuffix?: string;
}

export function StatusChip({ status, variant = 'chip', accentColour = null, srSuffix }: StatusChipProps) {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  const accessibleName = srSuffix ? `${config.label} — ${srSuffix}` : config.label;

  if (variant === 'token') {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 34,
          height: 24,
          padding: '0 5px',
          borderRadius: 6,
          backgroundColor: config.bg,
          color: config.color,
          fontFamily: '"Inter", sans-serif',
          fontSize: 10,
          fontWeight: 700,
          lineHeight: 1,
          borderBottom: accentColour ? `2px solid ${accentColour}` : '2px solid transparent',
          boxSizing: 'border-box',
        }}
      >
        <span aria-hidden="true">{config.short}</span>
        <span style={SR_ONLY}>{accessibleName}</span>
      </span>
    );
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '4px 8px',
        borderRadius: 20,
        backgroundColor: config.bg,
        color: config.color,
        fontFamily: '"Inter", sans-serif',
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      <Icon size={11} aria-hidden="true" />
      <span aria-hidden="true">{config.label}</span>
      <span style={SR_ONLY}>{accessibleName}</span>
    </span>
  );
}

export default StatusChip;
